// bibleUtils.ts

import { books } from '../bibleBooks'
import { INewVerse } from '../types'

const getNumbersWithinRange = (lo: number, hi: number): number[] => {
	const values = []
	for (let i = lo; i <= hi; i++) {
		values.push(i)
	}
	return values
}

const removeHyphenFromVerses = (verses: string[]): number[] => {
	const versesWithoutHyphen: number[] = []

	verses.forEach((el) => {
		if (/^[0-9]{1,3}[-–][0-9]{1,3}$/.test(el)) {
			const [loNum, hiNum] = el.split(/[-–]/).map(Number)
			versesWithoutHyphen.push(...getNumbersWithinRange(loNum, hiNum))
		} else {
			versesWithoutHyphen.push(parseInt(el))
		}
	})

	return versesWithoutHyphen.sort((a, b) => a - b)
}

export interface IBibleTextObj {
	bookId: string
	chapter: string
	verses: number[]
	bookName: string
}

// Normalize a book name/alias for matching: collapse ordinals and roman numerals
// ("1st/I/First" -> "1", "2nd/II/Second" -> "2", "3rd/III/Third" -> "3") and strip
// punctuation/spaces, so "2nd Kings", "II Kings" and "2 Kings" all match the same book.
const normalizeBook = (s: string): string =>
	s
		.toLowerCase()
		.trim()
		.replace(/\./g, '')
		.replace(/^(iii|3rd|third)\b/, '3')
		.replace(/^(ii|2nd|second)\b/, '2')
		.replace(/^(i|1st|first)\b/, '1')
		.replace(/[^a-z0-9]/g, '')

// Build the normalized alias -> book lookup once (book name + all abbreviations).
const bookByAlias: Record<string, (typeof books)[number]> = {}
books.forEach((book) => {
	bookByAlias[normalizeBook(book.name)] = book
	;(book.abbreviations || []).forEach((abbr) => {
		bookByAlias[normalizeBook(abbr)] = book
	})
})

const getVerseDetails = (txt: string) => {
	// Split the book name from the chapter/verse part. The book may be multiple
	// words ("Song of Solomon") or start with a digit ("1 Peter"), so match
	// greedily up to the first " <number>".
	const match = txt.trim().match(/^(.+?)\s+(\d.*)$/)
	const selectedBook = match ? match[1] : txt.trim()
	const rest = match ? match[2] : ''

	const book = bookByAlias[normalizeBook(selectedBook)]

	if (!book) {
		throw new Error(`Book ${selectedBook} not found`)
	}

	const bookId = book.id.toString()
	// A reference may be chapter-only ("Matthew 9") with no verse. In that case
	// verses is empty, which downstream treats as "show the whole chapter".
	const [chapter, versesUnsplit] = rest.split(':')
	const verses = versesUnsplit ? removeHyphenFromVerses(versesUnsplit.split(',')) : []

	return { bookId, chapter, verses, bookName: book.name } as IBibleTextObj
}

// filterArrayOfObjectsByArray
const filterByOnlySelectedVerses = (arrayOfObjects: INewVerse[], filter: number[]): INewVerse[] => {
	// No specific verses (e.g. a chapter-only reference like "Matthew 9") → show
	// the whole chapter.
	if (!filter || filter.length === 0) return arrayOfObjects
	return arrayOfObjects.filter((verse) => filter.includes(Number(verse.v)))
}

export { getNumbersWithinRange, removeHyphenFromVerses, getVerseDetails, filterByOnlySelectedVerses }
